import { inject, Injectable, signal } from '@angular/core';

import type { FilterValue } from '../../../shared-component/app-filter-select/app-filter-select.component';
import type { DashboardRole } from '../../../component/dashboard-page/dashboard-role.model';
import type { ManagementDashboardTab } from '../../../component/management-dashboard/management-dashboard-tabs.model';
import { AppStateResetService } from '../services/app-state-reset.service';
/**
 * The filter selections one dashboard view holds — the Sub Unit / Location /
 * License Type / Department / Period dropdowns and the From/To date window.
 * Mirrors the seven mutable filter fields on {@link ManagementDashboardComponent}
 * and {@link PerformerReviewerDashboardComponent} so a whole view's scope
 * round-trips as one object.
 */
export interface DashboardFilterSnapshot {
  selectedSubUnit: FilterValue;
  selectedLocationIds: (string | number)[] | null;
  selectedLicenseTypeIds: (string | number)[];
  selectedDepartmentIds: (string | number)[];
  selectedPeriod: FilterValue;
  fromDate: Date | null;
  toDate: Date | null;
}

/**
 * Root-provided persistence for the Dashboard's FILTER SELECTIONS, the last
 * active role tab, and the Management view's active in-page tab.
 *
 * The two dashboard views and their shell ({@link DashboardPageComponent}) are
 * destroyed on every navigation away from `/dashboard` (and the views also on a
 * role-tab switch), so their selections — held in plain component fields — used to
 * reset to defaults on return. This root singleton outlives that destroy and hands
 * the selections back on re-entry, so a returning user sees the dashboard exactly
 * as they left it: the same tab, the same dropdowns, and — because the views re-run
 * their existing loaders against the restored scope — the same cards / counts /
 * lists.
 *
 * It is the SELECTIONS counterpart to {@link DashboardDataStore} (which caches the
 * fetched DATA): same "a root store survives the component's destroy" shape, same
 * folder, kept separate so each has one job. Selections are stored per tab
 * (`'P'` / `'R'` / `'MGMT'` / `'CADMN'` / `'DEPT'` / `'AUDT'`) so switching tabs never carries
 * one tab's filters onto another — matching the pre-existing per-tab-independent
 * behaviour.
 *
 * Snapshots are copied in and out so a later field reassignment on a live view can
 * never mutate a stored (or a just-restored) snapshot.
 */
@Injectable({ providedIn: 'root' })
export class DashboardFilterStore {
  /** Persisted filter selections, one entry per dashboard tab key. */
  private readonly snapshots = new Map<string, DashboardFilterSnapshot>();

  /**
   * Persisted active Management in-page tab (Overview | Renewals | By State | By Team),
   * one entry per dashboard role-tab key (`managementApiRole`) — the same "remember one
   * enum per entry" job {@link lastActiveRole} does for the role tabs. Deliberately its
   * OWN map, NOT a field on {@link DashboardFilterSnapshot}: that snapshot is written by
   * BOTH dashboard views, so a required eighth field breaks the P/R view's `saveFilters`
   * call site under strict TS and an optional one puts a Management-only concept in a
   * shared shape (ADR 2026-08-10 management-dashboard-tabs §4.a).
   */
  private readonly activeTabs = new Map<string, ManagementDashboardTab>();

  /** The tab the user last had open, restored on the next Dashboard entry when it
      is still available to them. `null` until a view is first shown. */
  private lastActiveRole: DashboardRole | null = null;

  /**
   * Role view LIVE-published by the mounted {@link DashboardPageComponent} as soon
   * as it resolves which view to render (`applyRoleTabs()`), and cleared (see
   * {@link clearActiveRole}) as soon as that component unmounts. Distinct from
   * {@link lastActiveRole} above, which is only WRITTEN at `ngOnDestroy` (never
   * cleared) for the NEXT entry's restore: a bare `/dashboard` entry resolves its
   * view in place without ever changing the URL, so the shell's URL-based
   * sidebar-child derivation (`findActiveSidebarChildId`) cannot tell which
   * sub-item to tint. `AppComponent` reads this signal to fill that gap for a
   * multi-role user (see `AppComponent.deriveActiveSidebarChildId`) — it MUST be
   * cleared on unmount, or that fallback keeps tinting the just-left role's
   * sub-item on any other `/dashboard/*` URL, including the Dashboard-owned
   * Overview mounts that never render `DashboardPageComponent` at all.
   */
  private readonly _activeRole = signal<DashboardRole | null>(null);
  readonly activeRole = this._activeRole.asReadonly();

  /**
   * Registers {@link clear} for session teardown. This store restores selections
   * from a plain in-memory `Map` with no notion of WHOSE they are, so without this
   * the next user to open the Dashboard is handed the previous user's dropdowns,
   * date window and last active tab — the loaders then re-run against that scope,
   * so the counts and cards match the wrong user's filters too.
   */
  constructor() {
    inject(AppStateResetService).register(() => this.clear());
  }

  /**
   * Persists a view's current filter selections under its tab key. Called from the
   * view's `ngOnDestroy`, i.e. on navigation away and on a tab switch.
   */
  saveFilters(key: string, snapshot: DashboardFilterSnapshot): void {
    this.snapshots.set(key, this.copy(snapshot));
  }

  /**
   * Returns the filter selections persisted for a tab key, or `null` if the tab has
   * never been visited (a first visit keeps the view's field defaults). Called from
   * the view's `ngOnInit`, before its loaders run.
   */
  loadFilters(key: string): DashboardFilterSnapshot | null {
    const snapshot = this.snapshots.get(key);
    return snapshot ? this.copy(snapshot) : null;
  }

  /** Persists the last active role tab — called from the shell's `ngOnDestroy`. */
  saveActiveRole(role: DashboardRole): void {
    this.lastActiveRole = role;
  }

  /** The last active role tab, or `null` if none has been recorded yet. */
  loadActiveRole(): DashboardRole | null {
    return this.lastActiveRole;
  }

  /**
   * Persists the Management view's active in-page tab under its role-tab key, so a marked
   * Dashboard-UI return can reopen straight onto the tab the user left (rule 10's restore
   * behaviour, inherited from this store's existing keying/reset/teardown — see
   * {@link activeTabs}).
   */
  saveActiveTab(key: string, tab: ManagementDashboardTab): void {
    this.activeTabs.set(key, tab);
  }

  /**
   * The in-page tab persisted for a role-tab key, or `null` when none has been recorded
   * (a first visit keeps the host's `'overview'` default).
   */
  loadActiveTab(key: string): ManagementDashboardTab | null {
    return this.activeTabs.get(key) ?? null;
  }

  /** Publishes the role view now being rendered — called once per Dashboard
      entry, at the end of `applyRoleTabs()`, so it always matches what's on
      screen (see {@link activeRole}). */
  setActiveRole(role: DashboardRole): void {
    this._activeRole.set(role);
  }

  /**
   * Drops the LIVE role signal — called from the shell's `ngOnDestroy`, alongside (not
   * instead of) {@link saveActiveRole}. `isDashboardUrl()` matches every `/dashboard/*`
   * URL, including Dashboard-owned Overview mounts
   * (`/dashboard/compliance/...`) that never mount `DashboardPageComponent`; without this,
   * `AppComponent.deriveActiveSidebarChildId()`'s live-role fallback would keep reading
   * the role from the Dashboard page the user just navigated AWAY from and wrongly tint
   * that role's sidebar sub-item while an Overview page is on screen. `lastActiveRole`
   * (via `saveActiveRole`) is untouched, so a later bare `/dashboard` return still
   * restores the correct tab.
   */
  clearActiveRole(): void {
    this._activeRole.set(null);
  }

  /**
   * Drops every persisted filter snapshot, the last active role tab and the persisted
   * Management in-page tabs, so the next Dashboard entry falls back to defaults. Called
   * on a DEFAULT entry — i.e. when the user did NOT return via a Dashboard UI element
   * (the {@link FilterRestoreCoordinator} found no return flag), via the shell's
   * `restoreOnEnter` reset hook — and on session teardown (see the constructor).
   */
  clear(): void {
    this.snapshots.clear();
    this.lastActiveRole = null;
    this._activeRole.set(null);
    this.activeTabs.clear();
  }

  /** Defensive deep-enough copy: the three array fields AND the two `Date` fields are
      cloned so the stored snapshot is decoupled from the view's live, mutable arrays
      and dates (a spread alone would carry the same `Date`/array references through). */
  private copy(snapshot: DashboardFilterSnapshot): DashboardFilterSnapshot {
    return {
      ...snapshot,
      selectedLocationIds: snapshot.selectedLocationIds ? [...snapshot.selectedLocationIds] : null,
      selectedLicenseTypeIds: [...snapshot.selectedLicenseTypeIds],
      selectedDepartmentIds: [...snapshot.selectedDepartmentIds],
      fromDate: snapshot.fromDate ? new Date(snapshot.fromDate) : null,
      toDate: snapshot.toDate ? new Date(snapshot.toDate) : null,
    };
  }
}
